import React, { useEffect, useMemo, useState } from "react";
import { PageContainer } from "../components/Container/PageContainer";
import { Box, Stack, TextField, Button, Typography, Grid, Switch, FormControlLabel, Divider, Card, CardContent } from "@mui/material";
import { backfillCollectionTextAttachments, getAllCollection } from "../utils/pureDataFetch";

const AdminTextAttachmentBackfill = () => {
  const [threadIdsText, setThreadIdsText] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [selectedCollectionId, setSelectedCollectionId] = useState("");

  // recent collections (client-side sort, load 10 by 10 like MonthlyThreads)
  const [collectionsAll, setCollectionsAll] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [displayCount, setDisplayCount] = useState(10);

  const normalizeToMs = (v) => {
    if (v == null) return -Infinity;
    try {
      if (typeof v?.toDate === 'function') return v.toDate().getTime();
      if (typeof v === 'number') return v >= 1e12 ? v : v * 1000;
      const t = Date.parse(v);
      return isNaN(t) ? -Infinity : t;
    } catch (_) { return -Infinity; }
  };

  const getMaxThreadTimestampMs = (collection) => {
    try {
      if (!Array.isArray(collection?.threadList) || collection.threadList.length === 0) return -Infinity;
      let maxMs = -Infinity;
      for (const th of collection.threadList) {
        const v = th?.timestamp;
        let ms;
        try {
          if (v == null) ms = -Infinity;
          else if (typeof v?.toDate === 'function') ms = v.toDate().getTime();
          else if (typeof v === 'number') ms = v >= 1e12 ? v : v * 1000;
          else { const t = Date.parse(v); ms = isNaN(t) ? -Infinity : t; }
        } catch (_) { ms = -Infinity; }
        if (ms > maxMs) maxMs = ms;
      }
      return maxMs;
    } catch (_) { return -Infinity; }
  };

  const sortedCollections = useMemo(() => {
    const thresholdMs = Date.parse('2025-01-01T00:00:00Z');
    return [...collectionsAll].sort((a, b) => {
      const aLastRaw = normalizeToMs(a?.lastUpdated);
      const bLastRaw = normalizeToMs(b?.lastUpdated);
      const aFallback = aLastRaw < thresholdMs ? getMaxThreadTimestampMs(a) : -Infinity;
      const bFallback = bLastRaw < thresholdMs ? getMaxThreadTimestampMs(b) : -Infinity;
      const aLast = aFallback !== -Infinity ? aFallback : aLastRaw;
      const bLast = bFallback !== -Infinity ? bFallback : bLastRaw;
      if (bLast !== aLast) return bLast - aLast;
      const aCreated = normalizeToMs(a?.createdAt);
      const bCreated = normalizeToMs(b?.createdAt);
      return bCreated - aCreated;
    });
  }, [collectionsAll]);

  const collections = useMemo(() => sortedCollections.slice(0, displayCount), [sortedCollections, displayCount]);
  const hasMore = displayCount < sortedCollections.length;

  useEffect(() => {
    const fetchAll = async () => {
      setLoadingList(true);
      try {
        const all = await getAllCollection();
        setCollectionsAll(Array.isArray(all) ? all : []);
        setDisplayCount(10);
      } catch (e) {
        console.error('Failed to load collections:', e);
        setError(e?.message || String(e));
      } finally {
        setLoadingList(false);
      }
    };
    fetchAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parseThreadIds = () => {
    const raw = threadIdsText.trim();
    if (!raw) return [];
    return raw.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
  };

  const handleRun = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const threadIds = parseThreadIds();
      const res = await backfillCollectionTextAttachments(selectedCollectionId.trim(), { threadIds, dryRun });
      setResult(res);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setRunning(false);
    }
  };

  const formatTimestamp = (v) => {
    try {
      if (v == null) return '-';
      // Firestore Timestamp
      if (typeof v === 'object' && typeof v.toDate === 'function') {
        const d = v.toDate();
        return isNaN(d?.getTime?.()) ? '-' : d.toISOString();
      }
      if (typeof v === 'number') {
        const ms = v > 1e12 ? v : v * 1000;
        const d = new Date(ms);
        return isNaN(d.getTime()) ? '-' : d.toISOString();
      }
      if (typeof v === 'string') {
        const n = Number(v);
        if (!Number.isNaN(n)) {
          const ms = n > 1e12 ? n : n * 1000;
          const d = new Date(ms);
          return isNaN(d.getTime()) ? '-' : d.toISOString();
        }
        const d = new Date(v);
        return isNaN(d.getTime()) ? v : d.toISOString();
      }
      return '-';
    } catch (_) {
      return '-';
    }
  };

  return (
    <PageContainer>
      <Stack spacing={3} sx={{ width: "100%" }}>
        <Typography variant="h4" fontWeight={700}>Text Attachment Backfill</Typography>
        <Grid container spacing={2}>
          <Grid item size={{ xs: 12, md: 6 }}>
            <TextField
              label="Thread IDs (optional, comma/space separated)"
              value={threadIdsText}
              onChange={(e) => setThreadIdsText(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={<Switch checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />}
              label="Dry-run (no save)"
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 6 }}>
            <Button variant="contained" onClick={handleRun} disabled={running || !selectedCollectionId.trim()}>
              {running ? "Running..." : dryRun ? "Preview" : "Run Backfill"}
            </Button>
          </Grid>
        </Grid>

        <Divider />

        <Stack spacing={2}>
          <Typography variant="h6">Recent Collections</Typography>
          <Grid container spacing={1}>
            {collections.map((c) => (
              <Grid key={c.id} item size={{ xs: 12, md: 6 }}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" fontWeight={700} noWrap>
                        {c.title || '(untitled)'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        @{c.username || 'unknown'} • {c.threadList?.length || 0} threads
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        lastUpdated: {c.lastUpdated ? formatTimestamp(c.lastUpdated) : formatTimestamp(c.createdAt)}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Button size="small" variant={selectedCollectionId === c.id ? 'contained' : 'outlined'} onClick={() => setSelectedCollectionId(c.id)}>
                          {selectedCollectionId === c.id ? 'Selected' : 'Select'}
                        </Button>
                        <Button size="small" variant="outlined" onClick={async () => { setSelectedCollectionId(c.id); setDryRun(true); await handleRun(); }}>
                          Preview
                        </Button>
                        <Button size="small" variant="contained" onClick={async () => { setSelectedCollectionId(c.id); setDryRun(false); await handleRun(); }}>
                          Backfill
                        </Button>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box>
            <Button variant="text" onClick={() => setDisplayCount((c) => Math.min(c + 10, sortedCollections.length))} disabled={!hasMore || loadingList}>
              {loadingList ? 'Loading...' : hasMore ? 'Load more' : 'No more'}
            </Button>
          </Box>
        </Stack>

        {error && (
          <Typography color="error">{error}</Typography>
        )}

        {result && (
          <Stack spacing={2}>
            <Typography variant="h6">Result</Typography>
            <Typography variant="body2">Collection: {result.collectionId}</Typography>
            <Typography variant="body2">Processed: {result.processed}</Typography>
            <Typography variant="body2">Updated: {result.updated}</Typography>
            <Box sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 1, maxHeight: 320, overflow: "auto" }}>
              <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(result.diffs, null, 2)}</pre>
            </Box>
          </Stack>
        )}
      </Stack>
    </PageContainer>
  );
};

export default AdminTextAttachmentBackfill;


